import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Layers, Gauge, Navigation, Check, X } from "lucide-react";

const Panel = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-amber-300 font-semibold">
      <Icon className="w-3.5 h-3.5" /> {title}
    </div>
    {children}
  </div>
);

/** Chips multiselección (provincias / ciudades / campañas). */
function Chips({ items, selected, onToggle, render, testPrefix, empty = "Sin datos" }) {
  if (!items.length) return <div className="text-[11px] text-zinc-500 italic">{empty}</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => {
        const on = selected.includes(it.key);
        return (
          <button key={it.key} type="button" onClick={() => onToggle(it.key)} data-testid={`${testPrefix}-${it.key}`}
            className={`text-[11px] px-2 py-1 rounded-md border transition-colors inline-flex items-center gap-1 ${
              on ? "bg-amber-500/20 border-amber-500/60 text-amber-100" : "bg-zinc-950/60 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"}`}>
            {on && <Check className="w-3 h-3" />}{render(it)}
          </button>
        );
      })}
    </div>
  );
}

export default function PanelFiltros({ opciones, f, setF, onAnalizar, loading }) {
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggle = (k, key) => setF((prev) => ({ ...prev, [k]: prev[k].includes(key) ? prev[k].filter((x) => x !== key) : [...prev[k], key] }));
  const ciudades = (opciones?.ciudades || []).filter((c) => !f.provincias.length || !c.provincia_key || f.provincias.includes(c.provincia_key));
  const usarNave = () => set("inicio", { lat: String(opciones.nave_dnf.lat), lon: String(opciones.nave_dnf.lon), preset: "nave" });

  return (
    <div className="space-y-3" data-testid="reparto-form">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel icon={MapPin} title="Ámbito" testId="panel-ambito">
          <div className="text-[10px] text-zinc-500 mb-1">Provincias <span className="text-zinc-600">(vacío = todas)</span></div>
          <Chips items={opciones?.provincias || []} selected={f.provincias} onToggle={(k) => toggle("provincias", k)} testPrefix="prov"
            render={(p) => <>{p.label} <span className="opacity-60 font-mono">{p.n}</span></>} />
          <div className="text-[10px] text-zinc-500 mt-3 mb-1">Ciudades <span className="text-zinc-600">(vacío = todas las de la provincia)</span></div>
          <Chips items={ciudades} selected={f.ciudades} onToggle={(k) => toggle("ciudades", k)} testPrefix="ciu"
            render={(c) => <>{c.label} <span className="opacity-60 font-mono">{c.n}</span></>} />
          {(f.provincias.length > 0 || f.ciudades.length > 0) && (
            <button type="button" onClick={() => setF((p) => ({ ...p, provincias: [], ciudades: [] }))} data-testid="limpiar-ambito"
              className="mt-2 text-[10px] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"><X className="w-3 h-3" /> Limpiar ámbito</button>
          )}
        </Panel>

        <Panel icon={Layers} title="Trabajos · periodo de montaje" testId="panel-trabajos">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Desde</Label>
              <Input type="date" value={f.fecha_inicio} data-testid="fecha-inicio" className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                onChange={(e) => setF((p) => ({ ...p, fecha_inicio: e.target.value, fecha_fin: p.fecha_fin && p.fecha_fin < e.target.value ? e.target.value : p.fecha_fin, fecha_ruta: e.target.value }))} />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Hasta</Label>
              <Input type="date" value={f.fecha_fin} min={f.fecha_inicio || undefined} data-testid="fecha-fin" className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                onChange={(e) => set("fecha_fin", e.target.value)} />
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mb-2">Se usa la <b>fecha de montaje</b> de cada línea (programada o cita), nunca la fecha de creación de la petición. Ambos extremos incluidos.</div>
          <div className="flex flex-col gap-1.5 mb-3 text-[11px]">
            <label className="inline-flex items-center gap-1.5 text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={f.incluir_atrasados} onChange={(e) => set("incluir_atrasados", e.target.checked)} data-testid="incluir-atrasados" />
              Incluir trabajos <b>atrasados</b> <span className="text-zinc-500">(montaje anterior a «Desde» y aún pendiente)</span>
            </label>
            <label className="inline-flex items-center gap-1.5 text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={f.incluir_sin_fecha} onChange={(e) => set("incluir_sin_fecha", e.target.checked)} data-testid="incluir-sin-fecha" />
              Incluir trabajos <b>sin fecha asignada</b>
            </label>
            <label className="inline-flex items-center gap-1.5 text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={f.solo_material_listo} onChange={(e) => set("solo_material_listo", e.target.checked)} data-testid="solo-material-listo" />
              Sólo material apto para montar
            </label>
            <label className="inline-flex items-center gap-1.5 text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={f.usar_fecha_objetivo} onChange={(e) => set("usar_fecha_objetivo", e.target.checked)} data-testid="usar-fecha-objetivo" />
              Si la línea no tiene fecha, usar la fecha objetivo de la campaña como fecha de montaje
            </label>
          </div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] text-zinc-500">Campañas / peticiones <span className="text-zinc-600">(vacío = todas las elegibles)</span></div>
            {f.peticion_ids.length > 0 && <button type="button" onClick={() => set("peticion_ids", [])} data-testid="limpiar-campanas" className="text-[10px] text-zinc-500 hover:text-zinc-300">Limpiar</button>}
          </div>
          <div className="max-h-36 overflow-y-auto pr-1">
            <Chips items={(opciones?.campanas || []).map((c) => ({ ...c, key: c.id }))} selected={f.peticion_ids} onToggle={(k) => toggle("peticion_ids", k)} testPrefix="camp"
              empty="No hay campañas con montajes pendientes"
              render={(c) => <><span className="font-mono">{c.codigo}</span>{(c.marca || c.nombre) && <span className="opacity-75 truncate max-w-[160px]">· {c.marca || c.nombre}</span>}<span className="opacity-60 font-mono">{c.n_lineas}</span></>} />
          </div>
        </Panel>

        <Panel icon={Gauge} title="Capacidad" testId="panel-capacidad">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Máx. paradas por ruta <span className="text-red-300">(límite duro)</span></Label>
              <Input type="number" min="1" max="200" value={f.max_paradas} onChange={(e) => set("max_paradas", e.target.value)} data-testid="max-paradas"
                className="bg-zinc-950 border-zinc-800 h-9" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Nº de rutas deseado <span className="text-zinc-600">(opcional)</span></Label>
              <Input type="number" min="1" max="30" value={f.n_grupos} placeholder="auto" onChange={(e) => set("n_grupos", e.target.value)} data-testid="n-grupos-input"
                className="bg-zinc-950 border-zinc-800 h-9" />
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">Se generan como mínimo ⌈paradas / máx⌉ rutas. Si pides menos, se avisa y se respeta el límite.</div>
        </Panel>

        <Panel icon={Navigation} title="Salida" testId="panel-salida">
          <div className="flex gap-1.5 mb-2">
            <Button type="button" size="sm" variant={f.inicio.preset === "nave" ? "default" : "outline"} onClick={usarNave} data-testid="salida-nave"
              className={f.inicio.preset === "nave" ? "bg-amber-500 text-zinc-900 hover:bg-amber-400 h-8 text-xs" : "bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs"}>
              Nave DNF
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => set("inicio", { lat: "", lon: "", preset: "custom" })} data-testid="salida-custom"
              className={f.inicio.preset === "custom" ? "border-amber-500/60 text-amber-200 bg-amber-500/10 h-8 text-xs" : "bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs"}>
              Personalizado
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" step="0.0001" value={f.inicio.lat} placeholder="Latitud" data-testid="inicio-lat"
              onChange={(e) => set("inicio", { ...f.inicio, lat: e.target.value, preset: "custom" })} className="bg-zinc-950 border-zinc-800 h-9" />
            <Input type="number" step="0.0001" value={f.inicio.lon} placeholder="Longitud" data-testid="inicio-lon"
              onChange={(e) => set("inicio", { ...f.inicio, lon: e.target.value, preset: "custom" })} className="bg-zinc-950 border-zinc-800 h-9" />
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">{opciones?.nave_dnf?.label || "Coordenadas del punto de salida (opcional)"}</div>
        </Panel>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 justify-end">
        <Button onClick={onAnalizar} disabled={loading} data-testid="analizar-btn"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
          🔎 Analizar periodo
        </Button>
      </div>
    </div>
  );
}
